import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  getAdminSocietyDetail,
  AdminSocietyDetail as AdminSocietyDetailData,
  getSocietyPromotionCategoryOrder,
  saveSocietyPromotionCategoryOrder,
  SocietyPromotionCategoryRow,
} from '../services/api';
import { HiChevronRight } from '../components/Icons';
import './AdminSocietyDetail.css';

const AdminSocietyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const societyId = id ? parseInt(id, 10) : NaN;
  const [data, setData] = useState<AdminSocietyDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [catOrderRows, setCatOrderRows] = useState<SocietyPromotionCategoryRow[]>([]);
  const [orderInputs, setOrderInputs] = useState<Record<number, number>>({});
  const [catOrderLoading, setCatOrderLoading] = useState(false);
  const [catOrderSaving, setCatOrderSaving] = useState(false);
  const [catOrderError, setCatOrderError] = useState('');

  useEffect(() => {
    if (isNaN(societyId)) {
      setError('Invalid society');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await getAdminSocietyDetail(societyId);
        if (!cancelled) setData(res.data);
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load society');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [societyId]);

  useEffect(() => {
    if (isNaN(societyId)) return;
    let cancelled = false;
    (async () => {
      try {
        setCatOrderLoading(true);
        setCatOrderError('');
        const res = await getSocietyPromotionCategoryOrder(societyId);
        if (cancelled) return;
        setCatOrderRows(res.data);
        const m: Record<number, number> = {};
        res.data.forEach((c) => {
          m[c.id] = c.display_order;
        });
        setOrderInputs(m);
      } catch (e) {
        if (!cancelled) {
          setCatOrderError(e instanceof Error ? e.message : 'Failed to load category order');
        }
      } finally {
        if (!cancelled) setCatOrderLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [societyId]);

  const handleSaveCategoryOrder = async () => {
    if (isNaN(societyId) || catOrderRows.length === 0) return;
    setCatOrderSaving(true);
    setCatOrderError('');
    try {
      const orders = catOrderRows.map((c) => ({
        promotion_category_id: c.id,
        display_order: orderInputs[c.id] ?? c.display_order,
      }));
      await saveSocietyPromotionCategoryOrder(societyId, orders);
      const res = await getSocietyPromotionCategoryOrder(societyId);
      setCatOrderRows(res.data);
      const m: Record<number, number> = {};
      res.data.forEach((c) => {
        m[c.id] = c.display_order;
      });
      setOrderInputs(m);
    } catch (e) {
      setCatOrderError(e instanceof Error ? e.message : 'Failed to save order');
    } finally {
      setCatOrderSaving(false);
    }
  };

  if (loading) {
    return <div className="admin-society-detail-loading">Loading society…</div>;
  }

  if (error || !data) {
    return (
      <div className="admin-society-detail">
        <Link to="/admin/societies" className="admin-society-detail-back">
          ← Back to societies
        </Link>
        <div className="admin-society-detail-error">{error || 'Not found'}</div>
      </div>
    );
  }

  const { society, flats, activity_logs } = data;

  const copyPasscode = async () => {
    const code = society.society_passcode?.trim();
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      // ignore
    }
  };

  return (
    <div className="admin-society-detail">
      <Link to="/admin/societies" className="admin-society-detail-back">
        ← Back to societies
      </Link>

      <header className="admin-society-detail-header">
        <h2>{society.name}</h2>
        <p className="admin-society-detail-meta">
          {[society.city, society.state].filter(Boolean).join(', ')}
          {society.pincode != null && society.pincode !== '' ? ` · ${society.pincode}` : ''}
        </p>
        <div className="admin-society-detail-quick-links">
          <Link to={`/admin/societies/${society.id}/settings`} className="admin-society-detail-quick-link">
            Society Settings <HiChevronRight size={16} />
          </Link>
          <Link to={`/admin/societies/${society.id}/flat-members`} className="admin-society-detail-quick-link">
            Flat Members <HiChevronRight size={16} />
          </Link>
        </div>
      </header>

      <section className="admin-society-detail-card">
        <h3>Basic information</h3>
        <dl className="admin-society-detail-dl">
          <div className="admin-society-detail-passcode-row">
            <dt>Invitation passcode</dt>
            <dd>
              {society.society_passcode ? (
                <span className="admin-society-detail-passcode-wrap">
                  <code className="admin-society-detail-passcode">{society.society_passcode}</code>
                  <button
                    type="button"
                    className="admin-society-detail-passcode-copy"
                    onClick={copyPasscode}
                  >
                    Copy
                  </button>
                </span>
              ) : (
                '—'
              )}
            </dd>
          </div>
          <div>
            <dt>Location</dt>
            <dd>{society.location || '—'}</dd>
          </div>
          <div>
            <dt>Country</dt>
            <dd>{society.country || '—'}</dd>
          </div>
          <div>
            <dt>Type</dt>
            <dd>{society.type || '—'}</dd>
          </div>
          <div>
            <dt>Coordinates</dt>
            <dd>{society.latlong || '—'}</dd>
          </div>
          <div>
            <dt>Created</dt>
            <dd>{society.created_at ? new Date(society.created_at).toLocaleString() : '—'}</dd>
          </div>
        </dl>
      </section>

      <section className="admin-society-detail-card">
        <h3>Home category order</h3>
        <p className="admin-society-detail-section-intro">
          Order of promotion category modules on the app home screen for this society. Lower numbers appear first.
          Names and subtext are edited under <strong>Category modules</strong> in the admin menu. If you never save
          here, the global default order applies.
        </p>
        {catOrderError && <div className="admin-society-detail-cat-order-error">{catOrderError}</div>}
        {catOrderLoading ? (
          <p className="admin-society-detail-cat-order-loading">Loading categories…</p>
        ) : catOrderRows.length === 0 ? (
          <p className="admin-society-detail-empty">No active categories to order.</p>
        ) : (
          <>
            <div className="admin-society-detail-table-wrap">
              <table className="admin-society-detail-table admin-society-detail-cat-order-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Subtext</th>
                    <th>Order</th>
                    <th>Global default</th>
                  </tr>
                </thead>
                <tbody>
                  {catOrderRows.map((c) => (
                    <tr key={c.id}>
                      <td className="cell-strong">{c.name}</td>
                      <td className="admin-society-detail-subtext">{c.description || '—'}</td>
                      <td>
                        <input
                          type="number"
                          className="admin-society-detail-order-input"
                          value={orderInputs[c.id] ?? c.display_order}
                          onChange={(e) =>
                            setOrderInputs((prev) => ({
                              ...prev,
                              [c.id]: parseInt(e.target.value, 10) || 0,
                            }))
                          }
                        />
                      </td>
                      <td className="cell-muted">{c.global_display_order ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              className="admin-society-detail-save-order"
              onClick={() => void handleSaveCategoryOrder()}
              disabled={catOrderSaving}
            >
              {catOrderSaving ? 'Saving…' : 'Save category order'}
            </button>
          </>
        )}
      </section>

      <section className="admin-society-detail-card">
        <h3>Flats</h3>
        <p className="admin-society-detail-section-intro">
          One row per flat. Open a flat to see residents, flat details, and activity for the flat and each resident
          (logins, links, guest visits for that flat).
        </p>
        <div className="admin-society-detail-table-wrap">
          <table className="admin-society-detail-table">
            <thead>
              <tr>
                <th>Block</th>
                <th>Flat</th>
                <th>Floor</th>
                <th>Area</th>
                <th>Residents</th>
                <th className="cell-actions">Details</th>
              </tr>
            </thead>
            <tbody>
              {flats.length === 0 ? (
                <tr>
                  <td colSpan={6} className="admin-society-detail-empty">
                    No flats in this society.
                  </td>
                </tr>
              ) : (
                flats.map((flat) => {
                  const n = flat.users.length;
                  const namesJoined = flat.users.map((u) => u.name).join(', ');
                  const namesPreview =
                    namesJoined.length > 72 ? `${namesJoined.slice(0, 72)}…` : namesJoined;
                  const residentsLabel =
                    n === 0 ? 'None mapped' : `${n} — ${namesPreview}`;
                  return (
                    <tr key={flat.id}>
                      <td>{flat.block_name ?? '—'}</td>
                      <td className="cell-strong">{flat.flat_number}</td>
                      <td>{flat.floor}</td>
                      <td>{flat.area != null ? flat.area : '—'}</td>
                      <td className="cell-contact">{residentsLabel}</td>
                      <td className="cell-actions">
                        <Link
                          to={`/admin/societies/${society.id}/flats/${flat.id}`}
                          className="admin-society-flat-open"
                          title="View flat, residents, and activity"
                        >
                          Open <HiChevronRight size={18} />
                        </Link>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="admin-society-detail-card">
        <h3>Society activity log</h3>
        <p className="admin-society-detail-section-intro">
          Society-wide timeline: logins, flat links, guest visits, announcements, and society transactions (newest
          first). For flat-specific and per-resident timelines, open a flat from the table above.
        </p>
        <ul className="admin-society-detail-activity">
          {activity_logs.length === 0 ? (
            <li className="admin-society-detail-empty">No activity recorded yet.</li>
          ) : (
            activity_logs.map((log, idx) => (
              <li key={`${log.occurred_at}-${idx}`} className="admin-society-detail-activity-item">
                <div className="activity-time">{new Date(log.occurred_at).toLocaleString()}</div>
                <div className="activity-body">
                  <span className={`activity-type activity-type-${log.event_type}`}>{log.event_type.replace(/_/g, ' ')}</span>
                  <div className="activity-title">{log.title}</div>
                  {log.detail && <div className="activity-detail">{log.detail}</div>}
                  {log.actor_name && <div className="activity-actor">By {log.actor_name}</div>}
                </div>
              </li>
            ))
          )}
        </ul>
      </section>
    </div>
  );
};

export default AdminSocietyDetail;
